/**
 * Проверка русского текста на признаки нейросети.
 *
 * Происхождение. Правила и порядок подсчёта взяты из humanizer-ru
 * (github.com/ilyautov/humanizer-ru, лицензия MIT), файл `docs/scan.js`.
 * Здесь перевод на TypeScript: типы, наши имена и отдельный модуль
 * морфологии, которого в браузерной версии оригинала нет.
 *
 * Считает целиком браузер. Это не прихоть, а суть инструмента: текст
 * человека не уходит с его машины, проверка не стоит запроса к модели и
 * не тратит лимит. Поэтому же здесь нет ни одного обращения к сети,
 * кроме словаря морфологии, который грузится по отдельному нажатию.
 *
 * Что это НЕ такое: не детектор и не вероятность «написано нейросетью».
 * Оценка складывается из наблюдаемых признаков — штампов, ровного ритма,
 * одинаковых абзацев. Живой человек, пишущий канцеляритом, получит низкую
 * оценку заслуженно, и это ровно то, что нужно при вычитке.
 */

import PRAVILA from './pravila.json';

/** Насколько всё плохо. Порядок от лучшего к худшему. */
export type Polosa = 'chisto' | 'pravka' | 'rerajt';

/** Одно срабатывание: где и сколько раз. */
export interface Nahodka {
  /** Категория правила, например «Канцелярит». */
  kategoriya: string;
  /** Имя правила, оно же подпись для человека. */
  imya: string;
  kolichestvo: number;
  /** Отрезки в тексте: [начало, конец). Нужны для подсветки. */
  mesta: [number, number][];
}

/** Снятый балл с объяснением, за что именно. */
export interface Shtraf {
  prichina: string;
  bally: number;
}

export interface Ritm {
  predlozhenij: number;
  slov: number;
  srednyaya: number;
  /** Разброс длин предложений. Чем ровнее текст, тем ниже. */
  razbros: number;
  tire: number;
  voprosov: number;
  obryvki: number;
  obryvkiMax: number;
}

export interface Stroenie {
  abzacev: number;
  razbrosAbzacev: number;
  punktov: number;
  dolyaSpiskov: number;
}

export interface Morfologiya {
  sushestvitelnyh: number;
  glagolnyh: number;
  /** Отношение сущ./глаг. У нейросети около 3:1, у людей 2:1. */
  otnoshenie: number;
}

export interface Otchyot {
  ocenka: number;
  /**
   * Насколько всё плохо: `chisto` | `pravka` | `rerajt`.
   *
   * Код латиницей, хотя в оригинале здесь русское слово. Значение уходит
   * в ключ словаря, а ключ, совпадающий с человеческой подписью, ломается
   * от любой правки формулировки — и ломается молча, показывая человеку
   * сам ключ вместо текста.
   */
  polosa: Polosa;
  shtrafy: Shtraf[];
  zametki: string[];
  zaprety: Nahodka[];
  markery: Nahodka[];
  ritm: Ritm;
  stroenie: Stroenie;
  slov: number;
  /** Есть, только если словарь морфологии загружен. */
  morfologiya?: Morfologiya;
}

/** Части речи для пункта про канцелярит. Заполняется словарём при загрузке. */
export interface SlovarChastejRechi {
  sushestvitelnye: Set<string>;
  glagolnye: Set<string>;
}

/**
 * Пороги оценки.
 *
 * Перечислены поимённо, а не словарём `Record<string, number>`: числа
 * приходят из чужого JSON, и опечатка в имени дала бы `undefined` в
 * арифметике. Штраф при этом не упал бы, а молча стал `NaN`, и оценка
 * уехала бы в нуль без единой ошибки в консоли.
 */
interface Porogi {
  band_clean: number;
  band_edit: number;
  cv_human_target: number;
  staccato_max_words: number;
  staccato_min_run: number;
  staccato_penalty: number;
  staccato_penalty_max: number;
  para_min_count: number;
  para_cv_ai: number;
  listicle_min_items: number;
  listicle_share_ai: number;
  sterile_min_words: number;
  em_dash_name: string;
  copy_paste_category: string;
  /** Доля людей без единого штампа: [до скольки слов, процент]. */
  human_zero_share: [number, number][];
}

const R = PRAVILA as unknown as {
  hard_bans: { name: string; re: string; flags?: string }[];
  freq_bans: Record<string, number>;
  scanner: Record<string, { label: string; re?: string; lit?: string; flags?: string }[]>;
  genre_muted_bans: Record<string, string[]>;
  genre_muted_categories: Record<string, string[]>;
  score: Porogi;
  markdown: { gap: string; quote_max_words: number };
};

// --- Чужой текст в кавычках и коде не проверяем ---------------------------
// Цитата и листинг написаны не автором, и штрафовать его за чужие штампы
// значит врать. Порт `markdown.py` из humanizer-ru.

const PROBEL = R.markdown.gap;
const CITATA_MAX_SLOV = R.markdown.quote_max_words;
const ZABOR_OTKRYT = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const ZABOR_ZAKRYT = /^ {0,3}(`{3,}|~{3,})\s*$/;
const STROKA_CITATY = /^ {0,3}>/;
const NACHALO_BLOKA =
  /^ {0,3}(?:#{1,6}\s|[-*+]\s|\d{1,9}[.)]\s|(?:-{3,}|\*{3,}|_{3,})\s*$|`{3,}|~{3,})/;
const KOD_V_STROKE = /(`+)(?!`)([^`\n]+?)\1(?!`)/g;
const KAVYCHKI = /«[^«»]*»/g;

const zaterhet = (s: string) => s.replace(/[^\n]/g, PROBEL);

function zaboryKoda(stroki: string[]): [number, number][] {
  const oblasti: [number, number][] = [];
  let i = 0;
  while (i < stroki.length) {
    const m = ZABOR_OTKRYT.exec(stroki[i] ?? '');
    // Группы 1 и 2 в выражении обязательные, но типы об этом не знают.
    const zabor = m?.[1];
    if (!m || zabor === undefined || (zabor[0] === '`' && (m[2] ?? '').includes('`'))) {
      i += 1;
      continue;
    }
    const znak = zabor[0];
    const nuzhno = zabor.length;
    let j = i + 1;
    while (j < stroki.length) {
      const c = ZABOR_ZAKRYT.exec(stroki[j] ?? '');
      const zakryt = c?.[1];
      if (zakryt !== undefined && zakryt[0] === znak && zakryt.length >= nuzhno) break;
      j += 1;
    }
    if (j >= stroki.length) {
      i += 1;
      continue;
    }
    oblasti.push([i, j]);
    i = j + 1;
  }
  return oblasti;
}

function razobrat(text: string) {
  const stroki = text.split('\n');
  const vKode = new Set<number>();
  for (const [a, b] of zaboryKoda(stroki)) for (let k = a; k <= b; k++) vKode.add(k);

  const vCitate = new Set<number>();
  let vnutri = false;
  stroki.forEach((stroka, i) => {
    if (vKode.has(i)) {
      vnutri = false;
      return;
    }
    if (STROKA_CITATY.test(stroka)) {
      vnutri = true;
      vCitate.add(i);
    } else if (vnutri && stroka.trim() && !NACHALO_BLOKA.test(stroka)) vCitate.add(i);
    else vnutri = false;
  });
  return { stroki, vKode, vCitate };
}

/**
 * Заменяет чужие куски пробелами, сохраняя длину.
 *
 * Длину держим намеренно: места находок считаются по этому тексту, а
 * подсвечиваются в исходном. Сдвинь длину — и подсветка уедет.
 */
function zakrytChuzhoe(text: string): string {
  const { stroki, vKode, vCitate } = razobrat(text);
  const out = stroki.map((stroka, i) =>
    vKode.has(i) || vCitate.has(i)
      ? zaterhet(stroka)
      : stroka.replace(KOD_V_STROKE, (m) => zaterhet(m)),
  );
  return out
    .join('\n')
    .replace(KAVYCHKI, (m) =>
      m.slice(1, -1).trim().split(/\s+/).filter(Boolean).length <= CITATA_MAX_SLOV
        ? zaterhet(m)
        : m,
    );
}

/** То же, но куски выбрасываются: для ритма и абзацев длина не нужна. */
function vyrezatChuzhoe(text: string): string {
  const { stroki, vKode, vCitate } = razobrat(text);
  return stroki
    .filter((_, i) => !vKode.has(i) && !vCitate.has(i))
    .map((stroka) => stroka.replace(KOD_V_STROKE, ''))
    .join('\n');
}

// --- Поиск штампов --------------------------------------------------------

const ekranirovat = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const gotovye = new Map<string, RegExp>();

function vyrazhenie(isxod: string, bukvalno: boolean, flagi?: string): RegExp {
  const f = flagi || 'giu';
  const klyuch = (bukvalno ? 'L:' : 'R:') + f + ':' + isxod;
  let r = gotovye.get(klyuch);
  if (!r) {
    r = new RegExp(bukvalno ? ekranirovat(isxod) : isxod, f);
    gotovye.set(klyuch, r);
  }
  r.lastIndex = 0;
  return r;
}

function najtiVse(
  text: string,
  isxod: string,
  bukvalno: boolean,
  flagi?: string,
): [number, number][] {
  const r = vyrazhenie(isxod, bukvalno, flagi);
  const out: [number, number][] = [];
  let m: RegExpExecArray | null;
  while ((m = r.exec(text)) !== null) {
    out.push([m.index, m.index + Math.max(m[0].length, 1)]);
    // Пустое совпадение не двигает курсор само: без этого цикл вечный.
    if (m[0].length === 0) r.lastIndex += 1;
  }
  return out;
}

function najtiZaprety(text: string): Nahodka[] {
  const out: Nahodka[] = [];
  for (const b of R.hard_bans) {
    const mesta = najtiVse(text, b.re, false, b.flags);
    if (mesta.length)
      out.push({ kategoriya: 'Запрет', imya: b.name, kolichestvo: mesta.length, mesta });
  }
  return out;
}

function najtiMarkery(text: string): Nahodka[] {
  const out: Nahodka[] = [];
  for (const [kat, pravila] of Object.entries(R.scanner)) {
    for (const p of pravila) {
      const mesta =
        p.lit !== undefined
          ? najtiVse(text, p.lit, true)
          : najtiVse(text, p.re as string, false, p.flags);
      if (mesta.length)
        out.push({ kategoriya: kat, imya: p.label, kolichestvo: mesta.length, mesta });
    }
  }
  return out;
}

/** Убирает правила, законные для жанра: в законе «данный» это норма. */
function priglushit(
  nahodki: Nahodka[],
  zhanr: string | null,
  tihie: Record<string, string[]>,
): Nahodka[] {
  const imena = new Set(tihie[zhanr || ''] || []);
  return nahodki.filter((h) => !imena.has(h.imya) && !imena.has(h.kategoriya));
}

/** Отсеивает запреты, которые на таком объёме текста ещё не перебор. */
function dejstvuyushieZaprety(nahodki: Nahodka[], slov: number): Nahodka[] {
  return nahodki.filter((h) => {
    const na = R.freq_bans[h.imya];
    return !(na && (h.kolichestvo < 2 || h.kolichestvo <= slov / na));
  });
}

// --- Ритм -----------------------------------------------------------------
// Предложения режем по [.!?…], но точка после сокращения или инициала
// границей не является. Перевод строки сам по себе тоже не граница.

const SOKRASHENIYA = new Set(
  (
    'т.е т.д т.п т.к т.н др пр см ср г гг в вв ул д стр рис табл им св руб коп тыс млн млрд ' +
    'напр англ лат греч проф акад доц ст ч п пп гл изд ред сост пер кв корп обл р с сб вс пн вт чт пт'
  ).split(' '),
);
const BUKVA_ILI_CIFRA = /[\p{L}\p{N}]/u;

export function predlozheniya(text: string): string[] {
  const out: string[] = [];
  let nachalo = 0;
  const re = /[.!?…]+["»)\]]*(?=\s|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const konec = m.index + m[0].length;
    const do_ = text.slice(nachalo, m.index);
    const posledneeSlovo = (do_.match(/[\p{L}\p{N}.]+$/u) || [''])[0]
      .toLowerCase()
      .replace(/\.+$/, '');
    const dalee = text.slice(konec).match(/^\s*(\S)/);
    // Следующего знака может не быть: точка в самом конце текста.
    const sled = dalee?.[1] ?? '';
    const tolkoTochki = /^\.+$/.test(m[0].replace(/["»)\]]/g, ''));
    const sokrashenie =
      tolkoTochki &&
      (SOKRASHENIYA.has(posledneeSlovo) ||
        /^\p{Lu}$/u.test(do_.slice(-1)) ||
        (/^\d+$/.test(posledneeSlovo) && /\p{Ll}/u.test(sled)));
    const dalshaStrochnaya = tolkoTochki && /\p{Ll}/u.test(sled);
    if (sokrashenie || dalshaStrochnaya) continue;
    const s = text.slice(nachalo, konec).trim();
    if (s) out.push(s);
    nachalo = konec;
  }
  const hvost = text.slice(nachalo).trim();
  if (hvost) out.push(hvost);
  return out;
}

export function schitatSlova(s: string): number {
  return s.split(/\s+/).filter((t) => BUKVA_ILI_CIFRA.test(t)).length;
}

const srednee = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
const otklonenie = (a: number[]) => {
  if (a.length < 2) return 0;
  const mu = srednee(a);
  return Math.sqrt(srednee(a.map((x) => (x - mu) ** 2)));
};
const okruglit = (x: number, znakov: number) => {
  const k = 10 ** znakov;
  return Math.round(x * k) / k;
};

const REPLIKA = /^\s*[-–—«"']/;
const PROPUSTIT_STROKU = /^\s*(?:(?:[-*•]|\d+[.)])\s+|#)/;

/**
 * Цепочки обрывков: «Коротко. Точно. Ясно.»
 *
 * Это почерк не нейросети, а того, кто вывернул её ровный ритм наизнанку.
 * Реплики диалога, вопросы и пункты списков не считаются: там короткая
 * фраза естественна.
 */
function cepochkiObryvkov(text: string): [number, number] {
  const S = R.score;
  const cepochki: number[] = [];
  for (const stroka of text.replace(/\\/g, '').split('\n')) {
    if (!stroka.trim() || PROPUSTIT_STROKU.test(stroka)) continue;
    let tek = 0;
    for (const s of predlozheniya(stroka)) {
      const st = s.trim();
      const n = schitatSlova(st);
      if (!n) continue;
      // Последний знак есть всегда: пустые куски отсеяны выше по числу слов.
      const konecFrazy = st[st.length - 1] ?? '';
      if (n <= S.staccato_max_words && !REPLIKA.test(st) && !'?!:;'.includes(konecFrazy)) tek += 1;
      else {
        if (tek >= S.staccato_min_run) cepochki.push(tek);
        tek = 0;
      }
    }
    if (tek >= S.staccato_min_run) cepochki.push(tek);
  }
  return [cepochki.length, cepochki.length ? Math.max(...cepochki) : 0];
}

function schitatRitm(text: string): Ritm {
  const sp = predlozheniya(text);
  const dliny = sp.map(schitatSlova).filter((n) => n > 0);
  const mu = srednee(dliny);
  const sd = otklonenie(dliny);
  const [obryvki, obryvkiMax] = cepochkiObryvkov(text);
  return {
    predlozhenij: dliny.length,
    slov: dliny.reduce((x, y) => x + y, 0),
    srednyaya: okruglit(mu, 1),
    razbros: okruglit(mu ? sd / mu : 0, 3),
    tire: (text.match(/—/g) || []).length,
    voprosov: sp.filter((s) => s.trimEnd().endsWith('?')).length,
    obryvki,
    obryvkiMax,
  };
}

const PUNKT_SPISKA = /^\s*(?:[-*•]|\d+[.)])\s+\S/;

function schitatStroenie(text: string): Stroenie {
  const abzacy = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const dliny = abzacy.map((p) => predlozheniya(p).length).filter((n) => n > 0);
  const mu = srednee(dliny);
  const sd = otklonenie(dliny);
  const stroki = text.split('\n').filter((l) => l.trim());
  const punktov = stroki.filter((l) => PUNKT_SPISKA.test(l)).length;
  return {
    abzacev: dliny.length,
    razbrosAbzacev: okruglit(mu ? sd / mu : 0, 3),
    punktov,
    dolyaSpiskov: okruglit(stroki.length ? punktov / stroki.length : 0, 3),
  };
}

// --- Морфология -----------------------------------------------------------

const SLOVO_RU = /[а-яё-]+/gi;

/**
 * Считает отношение существительных к глаголам.
 *
 * Слово ищется в словаре как есть. «ё» приводим к «е» с обеих сторон:
 * в словаре OpenCorpora она обязательна, а в живом тексте её почти никто
 * не ставит, и без приведения «елка» просто не нашлась бы.
 *
 * Незнакомое слово не считается никем. Словарь собран до нынешней лексики,
 * и «токен», «нейросеть», «кринж» в нём отсутствуют. Молча относить их к
 * существительным нельзя: тогда любой текст про ИИ получал бы обвинение в
 * канцелярите за одну лишь тему.
 */
export function schitatMorfologiyu(text: string, slovar: SlovarChastejRechi): Morfologiya {
  let n = 0;
  let v = 0;
  const slova = text.toLowerCase().replace(/ё/g, 'е').match(SLOVO_RU) || [];
  for (const slovo of slova) {
    if (slovar.sushestvitelnye.has(slovo)) n += 1;
    else if (slovar.glagolnye.has(slovo)) v += 1;
  }
  return {
    sushestvitelnyh: n,
    glagolnyh: v,
    otnoshenie: v ? okruglit(n / v, 2) : n,
  };
}

// --- Оценка ---------------------------------------------------------------

const na100 = (skolko: number, slov: number) => (slov ? (skolko / slov) * 100 : 0);

function sklonenie(n: number, odna: string, dve: string, pyat: string): string {
  const d = n % 10;
  const dd = n % 100;
  if (d === 1 && dd !== 11) return odna;
  if (d >= 2 && d <= 4 && !(dd >= 12 && dd <= 14)) return dve;
  return pyat;
}

/** Порог канцелярита: у нейросетевого русского около 3:1, у людей 2:1. */
export const PREDEL_NOMINALNOSTI = 2.5;

/**
 * Собирает оценку.
 *
 * Считает то же и в том же порядке, что humanizer-ru, иначе числа на нашей
 * странице разошлись бы с их сканером, и объяснить расхождение было бы
 * нечем.
 */
export function proverit(
  text: string,
  zhanr: string | null = null,
  slovar?: SlovarChastejRechi,
): Otchyot {
  const S = R.score;
  const dlyaSlov = zakrytChuzhoe(text);
  const dlyaRitma = vyrezatChuzhoe(text);

  const ritm = schitatRitm(dlyaRitma);
  // Тире считаем по «закрытому» тексту: в листинге кода это не пунктуация.
  ritm.tire = (dlyaSlov.match(/—/g) || []).length;

  const vseZaprety = najtiZaprety(dlyaSlov);
  const vseMarkery = najtiMarkery(dlyaSlov);
  const stroenie = schitatStroenie(dlyaRitma);
  const morfologiya = slovar ? schitatMorfologiyu(dlyaRitma, slovar) : undefined;

  const slov = ritm.slov || 1;
  const markery = priglushit(vseMarkery, zhanr, R.genre_muted_categories);
  const tireTiho = (R.genre_muted_bans[zhanr || ''] || []).includes(S.em_dash_name);
  const shtrafy: Shtraf[] = [];
  const zametki: string[] = [];
  let ocenka = 100;

  const zaprety = priglushit(
    dejstvuyushieZaprety(vseZaprety, ritm.slov),
    zhanr,
    R.genre_muted_bans,
  );

  const frazy = zaprety
    .filter((h) => h.imya !== S.em_dash_name)
    .reduce((n, h) => n + h.kolichestvo, 0);
  if (frazy) {
    const p = Math.min(45, 12 * frazy);
    ocenka -= p;
    shtrafy.push({ prichina: `запрещённые обороты: ${String(frazy)}`, bally: -p });
  }

  const kopipasta = markery
    .filter((h) => h.kategoriya === S.copy_paste_category)
    .reduce((n, h) => n + h.kolichestvo, 0);
  if (kopipasta) {
    ocenka -= 60;
    shtrafy.push({ prichina: `следы копирования из чата: ${String(kopipasta)}`, bally: -60 });
  }

  const myagkie = markery
    .filter((h) => h.kategoriya !== S.copy_paste_category)
    .reduce((n, h) => n + h.kolichestvo, 0);
  if (myagkie) {
    const p = Math.min(30, Math.round(2 * na100(myagkie, slov)));
    if (p) {
      ocenka -= p;
      shtrafy.push({
        prichina: `штампы: ${String(myagkie)} (${na100(myagkie, slov).toFixed(1)} на 100 слов)`,
        bally: -p,
      });
    }
  }

  // Тире и правда частое у людей, поэтому штраф не сразу, а с допуском.
  const plotnostTire = tireTiho ? 0 : na100(ritm.tire, slov);
  if (plotnostTire > 2) {
    const p = Math.min(8, Math.round(3 * (plotnostTire - 2)));
    if (p) {
      ocenka -= p;
      shtrafy.push({
        prichina: `длинное тире: ${String(ritm.tire)} (${plotnostTire.toFixed(1)} на 100 слов)`,
        bally: -p,
      });
    }
  }

  if (ritm.predlozhenij >= 4 && ritm.razbros < S.cv_human_target) {
    const p = Math.min(
      20,
      Math.round(((S.cv_human_target - ritm.razbros) / S.cv_human_target) * 30),
    );
    if (p) {
      ocenka -= p;
      shtrafy.push({
        prichina: `одинаковые по длине предложения (разброс ${String(ritm.razbros)}, у людей от ${String(S.cv_human_target)})`,
        bally: -p,
      });
    }
  }

  if (ritm.obryvki) {
    const p = Math.min(S.staccato_penalty_max, S.staccato_penalty * ritm.obryvki);
    ocenka -= p;
    shtrafy.push({
      prichina: `рубленые фразы подряд: ${String(ritm.obryvki)} ${sklonenie(ritm.obryvki, 'цепочка', 'цепочки', 'цепочек')} (самая длинная ${String(ritm.obryvkiMax)})`,
      bally: -p,
    });
  }

  // Пункт про канцелярит есть, только если человек загрузил словарь.
  // Молча подставлять сюда догадку нельзя: на этом пункте держится разница
  // между «текст номинальный» и «текст живой», и ошибка здесь дороже
  // отсутствия пункта.
  if (morfologiya && morfologiya.otnoshenie > PREDEL_NOMINALNOSTI) {
    const p = Math.min(8, Math.round(((morfologiya.otnoshenie - PREDEL_NOMINALNOSTI) / 0.5) * 3));
    if (p) {
      ocenka -= p;
      shtrafy.push({
        prichina: `канцелярит: существительных к глаголам ${String(morfologiya.otnoshenie)} (норма до ${String(PREDEL_NOMINALNOSTI)})`,
        bally: -p,
      });
    }
  }

  if (stroenie.abzacev >= S.para_min_count && stroenie.razbrosAbzacev < S.para_cv_ai) {
    const p = Math.min(
      10,
      Math.round(((S.para_cv_ai - stroenie.razbrosAbzacev) / S.para_cv_ai) * 20),
    );
    if (p) {
      ocenka -= p;
      shtrafy.push({
        prichina: `одинаковые по длине абзацы (разброс ${String(stroenie.razbrosAbzacev)}, у людей от ${String(S.para_cv_ai)})`,
        bally: -p,
      });
    }
  }

  if (stroenie.punktov >= S.listicle_min_items && stroenie.dolyaSpiskov > S.listicle_share_ai) {
    const p = Math.min(12, Math.round((stroenie.dolyaSpiskov - S.listicle_share_ai) * 30));
    if (p) {
      ocenka -= p;
      shtrafy.push({
        prichina: `сплошной список: ${String(stroenie.punktov)} пунктов, ${String(Math.floor(stroenie.dolyaSpiskov * 100))}% строк`,
        bally: -p,
      });
    }
  }

  // Сто из ста — не цель, а редкий хвост распределения. На тексте в три сотни
  // слов совсем без штампов пишут 15% людей. Один такой текст неотличим, а
  // сайт, где все тексты стерильны, отличим сразу: у людей хвост есть.
  if (!(frazy || kopipasta || myagkie) && slov >= S.sterile_min_words) {
    // Список непустой, но типы об этом не знают: последняя строка
    // таблицы годится для текстов любой длины.
    const stroka =
      S.human_zero_share.find(([predel]) => slov < predel) ??
      S.human_zero_share[S.human_zero_share.length - 1];
    const dolya = Math.round(stroka?.[1] ?? 0);
    zametki.push(
      `Ни одного штампа. Так пишет ${String(dolya)}% людей на тексте в ${String(slov)} слов, остальные ${String(100 - dolya)}% что-нибудь да используют. Вычищать дальше незачем: цель не ноль, а обычная для жанра частота.`,
    );
  }

  const itog = Math.max(0, Math.min(100, Math.round(ocenka)));
  const polosa: Polosa =
    itog >= S.band_clean ? 'chisto' : itog >= S.band_edit ? 'pravka' : 'rerajt';

  return {
    ocenka: itog,
    polosa,
    shtrafy,
    zametki,
    zaprety,
    markery,
    ritm,
    stroenie,
    slov: ritm.slov,
    morfologiya,
  };
}

/** Жанры, в которых часть правил отключается как законная для регистра. */
export const ZHANRY = ['academic', 'legal', 'fiction', 'news'] as const;
